<template>
  <CForm @submit="submit">
    <CModal :title="$t('jobApproval.clientPrice')" icon="thumbs-up" @close="cancel">
      <template #body>
        <CField v-if="isQuote" type="text" v-model="formData.description" :label="$t('createJob.descriptionLabel')"/>

        <template v-else>
          <CLabel>{{ $t('jobApproval.jobTitle') }}</CLabel>
          <CCard class="u-mb-4">
            <h3>{{ job.getTitle() }}</h3>
          </CCard>
        </template>

        <CJobDmsPrice v-if="job.price.dmsAmountWithVat !== null" :job/>

        <CJobPriceAmountFields v-model:amountWithVat="formData.amountWithVat" v-model:amountWithoutVat="formData.amountWithoutVat"
                               v-model:warrantyStatus="formData.warrantyStatus" :job/>

        <div class="u-mt-4"/>

        <CField v-if="isQuote" v-model="formData.linkedJob" :label="$t('quotes.linkedJob')">
          <template #default="bindings">
            <COptionsDropdown up :options="[null, ...linkableJobs]" :modelValue="bindings['modelValue']" @update:modelValue="bindings['onUpdate:modelValue']"
                              :getLabel="o => o?.getTitle() ?? $t('quotes.linkedJobEmpty')"/>
          </template>
        </CField>

        <CField v-else type="radio" v-model="formData.approvalStatus" :label="$t('jobApproval.approval')" :options="enumValues(PriceApprovalStatus)"
                :disabledOptions="disabledApprovalStatuses" #option="{option}">
          <CFlex>
            <CPriceApprovalStatusIcon :status="option"/>
            {{ getPriceApprovalStatusLabel(option) }}
          </CFlex>
        </CField>
      </template>

      <template v-if="isQuote" #footer>
        <CButton type="reset" @click="cancel" class="c-modal-cancel">
          {{ $t('general.cancel') }}
        </CButton>

        <CButton theme="normal-danger" @click="removeQuote">
          <CIcon name="delete-trash" :size="18"/>
          {{ $t('quotes.delete') }}
        </CButton>

        <CButton type="submit" theme="main-primary">
          {{ $t('general.confirm') }}
        </CButton>
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { computed, reactive, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import COptionsDropdown from '@/dossier/components/COptionsDropdown.vue';
  import CModal from '@/general/components/CModal.vue';
  import Modals from '@/general/services/Modals';
  import { BasicModalResult, MODAL_CANCELLED } from '@/general/utils/Modal';
  import CCard from '@/shared/components/CCard.vue';
  import CFlex from '@/shared/components/CFlex.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CLabel from '@/shared/components/form/CLabel.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import { enumValues, ifPresent } from '@/shared/utils/typescript';
  import CPriceApprovalStatusIcon from '../components/approval/CPriceApprovalStatusIcon.vue';
  import JobCategory from '../constants/JobCategory';
  import PriceApprovalStatus from '../constants/PriceApprovalStatus';
  import Job, { JobPriceData } from '../models/Job';
  import JobContext from '../models/JobContext';
  import JobService from '../services/JobService';
  import { getPriceApprovalStatusLabel } from '../utils/labels';
  import CJobPriceAmountFields from '@/jobs/components/CJobPriceAmountFields.vue';
  import CJobDmsPrice from '@/jobs/components/CJobDmsPrice.vue';

  const emit = defineEmits<{ (event: 'close', result: JobPriceData | BasicModalResult): void }>();

  const props = defineProps<{
    job: Job;
    context: JobContext;
  }>();

  const i18n = useI18n();

  const linkableJobs = computed(() => props.context.getMechanicJobs());

  const formData = reactive({
    description: props.job.description.content,
    amountWithVat: props.job.price.amountWithVat,
    amountWithoutVat: props.job.price.amountWithoutVat,
    approvalStatus: props.job.priceApproval.status,
    linkedJob: ifPresent(props.job.linkedJob?.id, id => linkableJobs.value.find(j => j.id === id)) ?? null,
    warrantyStatus: props.job.warrantyStatus,
  }) as JobPriceData;

  const isQuote = computed(() => props.job.getCategory() === JobCategory.QUOTE);

  const disabledApprovalStatuses = computed(() => formData.amountWithVat !== null ? [] : [PriceApprovalStatus.APPROVED, PriceApprovalStatus.REFUSED]);

  watch(() => formData.amountWithVat, value => {
    if (value === null) formData.approvalStatus = PriceApprovalStatus.NOT_GIVEN;
  });

  async function submit() {
    emit('close', formData);
  }

  async function removeQuote() {
    await Modals.confirmDelete(i18n.t('quotes.deleteConfirmation'));
    await JobService.remove(props.job.id, props.context);
    cancel();
  }

  function cancel() {
    emit('close', MODAL_CANCELLED);
  }
</script>
